import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useEffect } from 'react'

import { fetchUserPositionsRequestAction } from '@root/entities/position'
import { tradingWalletAtom } from '@root/features/trade/trading/atoms/trading-wallet'

import { emptyReason, positionOf } from '../../model/my-position'
import {
  percent,
  price as priceOf,
  quantity,
  signedUsd,
  usd,
} from '../portfolio-screen/money'

import type { Network } from '@root/entities/network'
import type { PairModel } from '@root/entities/pair'
import type { AggregatedPositionModel } from '@root/entities/position'

/**
 * The only row on this page that is yours.
 *
 * Every other table here is about other people — who holds it, who made money,
 * who just traded. Without this one you have to open the portfolio in another
 * tab to answer the first question anybody has on a token page: am I already
 * in this, and at what.
 *
 * The read is the account's whole position list, which is how the app fills
 * its registry everywhere else; this screen then takes the one row for the
 * pair on screen. It fires when the tab is opened and not before.
 */
export const MyPositionTable: React.FC<{
  pair: PairModel
  network: Network.Network
}> = memo(({ pair, network }) => {
  const [position] = useAtom(pair.position)
  const [wallet] = useAtom(tradingWalletAtom)

  const loadPositions = useAction(fetchUserPositionsRequestAction)
  useEffect(() => {
    /* An empty walletIds asks for every wallet on the account, which is what
       "my position" means when more than one of them can hold this. */
    loadPositions({ network, limit: 100, offset: 0, walletIds: [] })
  }, [loadPositions, network])

  if (!position) {
    return <p className="soon">{emptyReason({ hasWallet: Boolean(wallet) })}</p>
  }

  return <PositionBody position={position} />
})

/**
 * The position's own figures.
 *
 * Apart because every field on the model is an atom and hooks cannot be called
 * conditionally — the row either exists, and then all of it is read, or it
 * does not and nothing is.
 */
const PositionBody: React.FC<{ position: AggregatedPositionModel }> = memo(
  ({ position }) => {
    const [remainingUsd] = useAtom(position.remainingSizeUsd)
    const [remainingTokens] = useAtom(position.remainingSizeCurrency)
    const [boughtUsd] = useAtom(position.boughtSizeUsd)
    const [buys] = useAtom(position.boughtTransactions)
    const [soldUsd] = useAtom(position.soldSizeUsd)
    const [sells] = useAtom(position.soldTransactions)
    const [entryPriceUsd] = useAtom(position.entryPriceUsd)
    const [entryMarketCap] = useAtom(position.entryMarketCapUsd)
    const [realised] = useAtom(position.realizedPnlUsd)
    const [unrealised] = useAtom(position.unrealizedPnlUsd)
    const [closedAt] = useAtom(position.closedAt)

    const reading = positionOf({
      remainingUsd: num(remainingUsd),
      remainingTokens: num(remainingTokens),
      boughtUsd: num(boughtUsd),
      buys: Number(buys),
      soldUsd: num(soldUsd),
      sells: Number(sells),
      entryPriceUsd: num(entryPriceUsd),
      entryMarketCap: num(entryMarketCap),
      realisedUsd: num(realised.pnlUsd),
      unrealisedUsd: num(unrealised.pnlUsd),
      closedAt: closedAt ?? undefined,
    })

    if (!reading) {
      return undefined
    }

    return (
      <div className="mkscroll">
        <div className="mypos">
          <div className="myhead">
            <b className="mono" style={{ color: pnlColour(reading.pnlUsd) }}>
              {signedUsd(reading.pnlUsd)}
            </b>
            <span
              className="mono pct"
              style={{ color: pnlColour(reading.pnlUsd) }}
            >
              {percent(reading.pnlPercent, { sign: true })}
            </span>
            <span className={reading.open ? 'state open' : 'state'}>
              {reading.open ? 'open' : 'closed'}
            </span>
          </div>

          <div className="mygrid">
            <Cell
              label="Holding"
              value={reading.open ? usd(reading.sizeUsd) : '—'}
              note={reading.open ? quantity(reading.sizeTokens) : undefined}
            />
            <Cell
              label="Bought"
              value={usd(reading.boughtUsd)}
              note={`${reading.buys} buys`}
            />
            <Cell
              label="Sold"
              value={usd(reading.soldUsd)}
              note={`${reading.sells} sells`}
            />
            <Cell label="Entry" value={priceOf(reading.entryPriceUsd)} />
            <Cell label="Entry cap" value={usd(reading.entryMarketCap)} />
          </div>
        </div>
      </div>
    )
  },
)

/** Reatom hands these over as BigNumbers; the reading wants plain numbers. */
const num = (
  value: { toNumber?: () => number } | number | undefined,
): number => {
  if (value === undefined) {
    return Number.NaN
  }
  if (typeof value === 'number') {
    return value
  }
  return value.toNumber ? value.toNumber() : Number.NaN
}

const pnlColour = (value: number): string => {
  if (!Number.isFinite(value) || value === 0) {
    return 'var(--t2)'
  }
  return value > 0 ? 'var(--up)' : 'var(--down)'
}

const Cell: React.FC<{ label: string; value: string; note?: string }> = memo(
  ({ label, value, note }) => (
    <div className="myc">
      <i>{label}</i>
      <b className="mono">{value}</b>
      {note ? <em className="mono">{note}</em> : undefined}
    </div>
  ),
)
